import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { CategoryType, CategoryTypeFilterList, CategoryTypeString, RelayType } from '@diritto/common/content/constants';
import { GlobalNavigationDirectionText, GlobalNavigationMenu } from '@diritto/common/navigation/constant';
import { RecentlyReadContentDto } from '@diritto/common/recentlyRead/dtos/recently-read.dto';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { PersonalContentRelayDto } from '@diritto/common/relay/dtos/personal-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Typography } from '@diritto/common/style/typography';
import { FeatureFlagName } from '@diritto/common/system/constants';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { FavoriteContentOrder } from '@diritto/common/userReaction/constants';
import { FavoriteContentDto } from '@diritto/common/userReaction/dtos/favorite-content.dto';
import { accurateItems, flatItems, toUniqueArray } from '@diritto/common/utils/array';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { ContentOrderTab } from 'app-components/my/ContentOrderTab';
import { FavoriteContentItem } from 'app-components/my/FavoriteContentItem';
import { FavoritePrivateContentItem } from 'app-components/my/FavoritePrivateContentItem';
import { ButtonSize, ButtonType } from 'constants/button';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetRelayFavoriteContentsInfiniteQuery } from 'queries/contents/useGetRelayFavoriteContentsInfiniteQuery';
import { EmptyList } from 'ui-components/common/EmptyList';
import { FilterMenuType } from 'ui-components/common/FilterItemList';
import { HomeLinkButton } from 'ui-components/common/HomeLinkButton';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { Loading } from 'ui-components/common/Loading';
import { useFeatureFlagConfig } from 'ui-hooks/useFeatureFlagConfig';
import { LocalStorage } from 'utils/LocalStorage';

const FavoriteOrderList = [
  { title: '업데이트순', type: FavoriteContentOrder.EpisodeUpdated },
  { title: '등록순', type: FavoriteContentOrder.LatestFavorite },
];

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.My,
};

export type FavoriteContentParams = {
  orderBy: FavoriteContentOrder;
  notiOnly: boolean;
  categoryType: CategoryType;
  showOnlyCompleted: boolean;
  showOnlyPrivate: boolean;
};

export const FavoriteContentListView = ({
  orderBy: initialOrderBy,
  notiOnly: initialNotiOnly,
  categoryType: initialCategoryType,
  showOnlyCompleted: initialShowOnlyCompleted,
  showOnlyPrivate: initialShowOnlyPrivate,
}: FavoriteContentParams) => {
  const router = useRouter();
  const { tracker } = useTrackingState();
  const { isActiveFlag } = useFeatureFlagConfig();
  const isFanFictionEnabled = isActiveFlag(FeatureFlagName.FanFiction);
  const categoryTypeFilterList = useMemo(
    () =>
      isFanFictionEnabled
        ? CategoryTypeFilterList
        : CategoryTypeFilterList.filter(type => type !== CategoryType.FanFiction),
    [isFanFictionEnabled],
  );

  const [params, setParams] = useState<FavoriteContentParams>({
    orderBy: initialOrderBy,
    notiOnly: initialNotiOnly,
    categoryType: initialCategoryType,
    showOnlyCompleted: initialShowOnlyCompleted,
    showOnlyPrivate: initialShowOnlyPrivate,
  });

  const isAllowPrivateContentFiltering = isActiveFlag(FeatureFlagName.AllowPrivateContentFiltering);

  const { isLoading, data, hasNextPage, fetchNextPage } = useGetRelayFavoriteContentsInfiniteQuery({
    categoryType: params.categoryType,
    orderBy: params.orderBy,
    notiOnly: params.showOnlyPrivate ? false : params.notiOnly,
    showOnlyCompleted: params.showOnlyPrivate ? false : params.showOnlyCompleted,
    showOnlyPrivate: params.showOnlyPrivate,
  });

  const { totalCount, contentIds, personalContents, episodes, contents, profiles } = useMemo(() => {
    const favoriteContentList = flatItems<FavoriteContentDto>('favoriteContents', data?.pages) ?? [];

    return {
      totalCount: data?.pages[0]?.totalCount,
      contentIds: toUniqueArray(favoriteContentList.map(it => it.contentId) ?? []),
      personalContents: accurateItems<PersonalContentRelayDto>('personalContents', data?.pages),
      episodes: accurateItems<EpisodeRelayDto>('episodes', data?.pages),
      contents: accurateItems<ContentRelayDto>('contents', data?.pages),
      profiles: accurateItems<ProfileLiteRelayDto>('profileLites', data?.pages),
    };
  }, [data?.pages]);

  const handleParamsChange = useCallback(
    (updatedParams: Partial<FavoriteContentParams>) => {
      const mergedParams = {
        ...params,
        ...updatedParams,
      };
      setParams(mergedParams);
      LocalStorage.setItem(LocalStorageKey.MyFavoriteContentsRefineType, JSON.stringify(mergedParams));
    },
    [params],
  );

  const handleChangeCategoryType = useCallback(
    (categoryType: CategoryType) => {
      tracker.track(TrackingEventType.SetCategoryFilter, { categoryType, trackerExtraInfo });
      handleParamsChange({ categoryType });
    },
    [handleParamsChange, tracker],
  );

  const handleChangeOrder = useCallback(
    (order: FavoriteContentOrder) => {
      tracker.track(TrackingEventType.SetOrderFilter, { orderBy: order, trackerExtraInfo });
      handleParamsChange({ orderBy: order });
    },
    [handleParamsChange, tracker],
  );

  const handleChangeNotiFilter = useCallback(() => {
    tracker.track(TrackingEventType.SetNotiOnlyFilter, { enabled: !params.notiOnly, trackerExtraInfo });
    handleParamsChange({ notiOnly: !params.notiOnly });
  }, [handleParamsChange, params.notiOnly, tracker]);

  const handleChangeShowOnlyCompleted = useCallback(() => {
    tracker.track(TrackingEventType.SetCompletedOnlyFilter, {
      enabled: !params.showOnlyCompleted,
      trackerExtraInfo,
    });
    handleParamsChange({ showOnlyCompleted: !params.showOnlyCompleted });
  }, [handleParamsChange, params.showOnlyCompleted, tracker]);

  const handleChangeShowPrivate = useCallback(() => {
    tracker.track(TrackingEventType.SetPrivateOnlyFilter, {
      enabled: !params.showOnlyPrivate,
      trackerExtraInfo,
    });
    handleParamsChange({ showOnlyPrivate: !params.showOnlyPrivate });
  }, [handleParamsChange, params.showOnlyPrivate, tracker]);

  const handleEditToggle = useCallback(() => {
    router.replace('/my/favorites/edit');
  }, [router]);

  const filterCategoryItems = useMemo(
    () =>
      categoryTypeFilterList.map(item => ({
        title: CategoryTypeString[item],
        value: item,
        checked: item === params.categoryType,
        onClick: () => handleChangeCategoryType(item),
      })),
    [categoryTypeFilterList, params.categoryType, handleChangeCategoryType],
  );

  const filterOrderItems = useMemo(
    () =>
      FavoriteOrderList.map(item => ({
        title: item.title,
        value: item.type,
        checked: item.type === params.orderBy,
        onClick: () => handleChangeOrder(item.type),
      })),
    [params.orderBy, handleChangeOrder],
  );

  const filterAddedItems = useMemo(
    () => [
      {
        title: '알림 설정 작품',
        checked: params.notiOnly ?? false,
        onClick: handleChangeNotiFilter,
        disabled: params.showOnlyPrivate,
      },
      {
        title: '완결',
        checked: params.showOnlyCompleted ?? false,
        onClick: handleChangeShowOnlyCompleted,
        disabled: params.showOnlyPrivate,
      },
    ],
    [
      params.notiOnly,
      params.showOnlyCompleted,
      handleChangeNotiFilter,
      handleChangeShowOnlyCompleted,
      params.showOnlyPrivate,
    ],
  );

  const filterShowPrivateItems = useMemo(
    () => [
      {
        title: '비공개',
        checked: params.showOnlyPrivate ?? false,
        onClick: handleChangeShowPrivate,
      },
    ],
    [params.showOnlyPrivate, handleChangeShowPrivate],
  );

  const filterMenuGroups = useMemo(
    () => [
      { key: 'order', type: FilterMenuType.CircleRadioButton, items: filterOrderItems },
      {
        key: 'additionalFilter',
        type: FilterMenuType.CircleCheckBoxButton,
        items: filterAddedItems,
      },
      ...(isAllowPrivateContentFiltering
        ? [
            {
              key: 'showPrivate',
              type: FilterMenuType.SwitchButton,
              items: filterShowPrivateItems,
              description: '일부 필터가 꺼졌어요.',
            },
          ]
        : []),
    ],
    [filterOrderItems, filterAddedItems, filterShowPrivateItems, isAllowPrivateContentFiltering],
  );

  const renderEmptyList = useCallback(() => {
    if (
      contentIds.length === 0 &&
      params.categoryType === CategoryType.All &&
      !params.notiOnly &&
      !params.showOnlyCompleted &&
      !params.showOnlyPrivate
    ) {
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <EmptyList description={'아직 좋아요한 작품이 없어요.\n마음에 드는 작품을 좋아해보세요.'}>
            <HomeLinkButton
              buttonType={ButtonType.SecondaryNeutral}
              buttonTypography={Typography.XXXSmall}
              className="inline-block"
              label={GlobalNavigationDirectionText[GlobalNavigationMenu.Explore]}
              size={ButtonSize.Small}
            />
          </EmptyList>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-[50vh] px-4">
        <EmptyList description="선택하신 조건에 맞는 작품이 없어요." />
      </div>
    );
  }, [contentIds.length, params.notiOnly, params.categoryType, params.showOnlyCompleted, params.showOnlyPrivate]);

  const renderContentList = useCallback(
    () => (
      <InfiniteScrollWrapper loadMore={fetchNextPage} hasNext={hasNextPage}>
        <div className="flex flex-col gap-2 mt-2 px-4">
          {contentIds.map((id, idx) => {
            const content: ContentRelayDto = contents[id];
            const profile =
              content?.relayType !== RelayType.Deleted && content.profileId ? profiles[content.profileId] : undefined;
            const personalContent = personalContents[content.id];

            const recentlyReadContent = personalContent?.recentlyReadContent as RecentlyReadContentDto | undefined;
            const episode = recentlyReadContent?.episodeId ? episodes[recentlyReadContent?.episodeId] : undefined;

            if ((profile && profile.isDeleted) || !content || content.relayType === RelayType.Deleted) {
              return null;
            }

            if (content.relayType === RelayType.Private) {
              return (
                <FavoritePrivateContentItem
                  key={id}
                  listItemIndex={idx}
                  content={content}
                  profile={profile}
                  personalContent={personalContent}
                />
              );
            }

            return (
              <FavoriteContentItem
                key={id}
                listItemIndex={idx}
                content={content}
                profile={profile}
                episode={episode}
                recentlyReadContent={recentlyReadContent}
                personalContent={personalContent}
              />
            );
          })}
        </div>
      </InfiniteScrollWrapper>
    ),
    [contentIds, contents, episodes, fetchNextPage, hasNextPage, personalContents, profiles],
  );

  if (isLoading) {
    return (
      <div className="flex h-[50vh] justify-center items-center">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <ContentOrderTab
        sectionText="작품"
        itemCount={totalCount}
        categoryItems={filterCategoryItems}
        filterMenuGroups={filterMenuGroups}
        categoryType={params.categoryType}
        onEditClick={handleEditToggle}
        isEditing={contentIds.length > 0}
        isFiltering={params.notiOnly || params.showOnlyCompleted || params.showOnlyPrivate}
      />
      {contentIds.length === 0 ? renderEmptyList() : renderContentList()}
    </>
  );
};
